import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { ApiHttpService } from 'src/app/core/services/apiHttpcall.service';
import { DownloadConfirmedPopupComponent } from '../download-confirmed-popup/download-confirmed-popup.component';

@Component({
  selector: 'import-popup',
  templateUrl: './import-popup.component.html',
  styleUrls: ['./import-popup.component.scss'],
})
export class ImportPopupComponent implements OnChanges {
  import_stage: string = 'ask_to_upload';
  value: number = 0;
  allowed_formats: string[] = ['csv', 'xlsx'];
  maxFileSize: number;
  file: string[];
  file_name: string = '';
  file_extension: string = '';
  uploadedFiles: any[] = []; // Array to store multiple uploaded files
  show_ImportDialog: boolean = false;
  dialog_header: string = 'Upload CSV';
  importError_Visible: boolean = false;
  genericError_Visible: boolean = false;
  importError_Message: string = '';
  Is_ValidFile: boolean = false;
  Is_OrderDetail: boolean = false;
  // global interval
  interval: any;

  @ViewChild('fubauto') fileUpload: any;
  @ViewChild('confirmedUpload_ref')
  confirmedUpload_ref: DownloadConfirmedPopupComponent;

  @Input() moduleName: string = '';
  @Input() importType: 'data' | 'image' | 'all'|'document' = 'data'; // 'data' for CSV/XLSX, 'image' for images, 'all' for all file types
  @Input() customHeader: string = ''; // Custom header text
  isSuccessful: boolean = false;
  errors: any;
  error_Visible: boolean = false;
  imagePreviewUrl: string = '';
  @Input('MaxFileSize') set _MaxFileSize(value: any) {
    this.maxFileSize = value ? value : 2000000;
  }

  @Output() relaodGrid: EventEmitter<any> = new EventEmitter();

  @HostListener('document:keydown.escape', ['$event'])
  onEscPressed(event: KeyboardEvent) {
    this.hide();
  }

  constructor(
    public dialogService: DialogService,
    private messageService: MessageService,
    private httpService: ApiHttpService
  ) {
    this.setImportConfiguration();
  }

  private setImportConfiguration(): void {
    if (this.importType === 'image') {
      this.allowed_formats = ['jpeg', 'jpg', 'png'];
      this.dialog_header = this.customHeader || 'Upload Files';
    } else if (this.importType === 'all') {
      // For all file types
      this.allowed_formats = ['jpeg', 'jpg', 'png', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt'];
      this.dialog_header = this.customHeader || 'Upload Files';
    }else if (this.importType === 'document') {
      this.allowed_formats = ['pdf', 'doc', 'docx', 'jpeg', 'jpg', 'png'];
      this.dialog_header = this.customHeader || 'Upload Documents';
    } else {
      // For data files (CSV/XLSX only)
      this.allowed_formats = ['csv', 'xlsx'];
      this.dialog_header = this.customHeader || 'Upload CSV';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['importType'] || changes['customHeader']) {
      this.setImportConfiguration();
    }
  }

  private createImagePreview(file: any): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.imagePreviewUrl = e.target.result;
    };
    reader.readAsDataURL(file);
  }

  private createFilePreview(file: any): string {
    if (file.type.startsWith('image/')) {
      return URL.createObjectURL(file);
    }
    return this.getFileIcon(file.type);
  }

  private getFileIcon(fileType: string): string {
    if (fileType.startsWith('image/')) {
      return 'assets/images/image.png';
    } else if (fileType.includes('pdf')) {
      return 'assets/images/pdf-icon.png';
    } else if (fileType.includes('word') || fileType.includes('document')) {
      return 'assets/images/doc-icon.png';
    } else if (fileType.includes('excel') || fileType.includes('spreadsheet')) {
      return 'assets/images/excel-logo.png';
    } else {
      return 'assets/images/image.png'; // Default to image icon
    }
  }

  private isValidFile(file: any): boolean {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    return this.allowed_formats.includes(fileExtension) && file.size <= this.maxFileSize;
  }
  onFileDrop(evnt: any) {
    if (evnt && this.value == 0) {
      this.file = evnt[0].file;
      this.file_extension = evnt[0].file.name.split('.')[1];
      this.file_name = evnt[0].file.name;
      if (this.importType === 'all') {
        // Handle multiple files for 'all' type
        const files = evnt.files;
        if (files && files.length > 0) {
          for (let i = 0; i < files.length; i++) {
            const file = files[i];
            if (this.isValidFile(file)) {
              // Check for duplicates
              const isDuplicate = this.uploadedFiles.some(existingFile =>
                existingFile.name === file.name && existingFile.size === file.size
              );

              if (!isDuplicate) {
                const fileInfo = {
                  file: file,
                  name: file.name,
                  size: file.size,
                  type: file.type,
                  preview: this.createFilePreview(file)
                };
                this.uploadedFiles.push(fileInfo);
              }
            }
          }
        }
      }
     else if (this.importType === 'image' || this.importType === 'document') {
        // For images, validate format first
        if (this.allowed_formats.includes(this.file_extension)) {
          this.import_stage = 'upload_progress';
          this.Is_ValidFile = true;
          this.importError_Visible = false;
           // Only create image preview for image files
           if (['jpeg', 'jpg', 'png'].includes(this.file_extension)) {
            this.createImagePreview(this.file);
          } else {
            this.imagePreviewUrl = ''; // Clear image preview for documents
          }
        } else {
          this.Is_ValidFile = false;
          this.importError_Visible = true;
          this.importError_Message = `Invalid file format! Supported formats are: ${this.allowed_formats.join(', ').toUpperCase()}`;
          this.imagePreviewUrl = ''; // Clear any previous image preview
          this.messageService.add({
            severity: 'error',
            summary: 'Invalid File Format',
            detail: this.importError_Message,
            key: 'app_notiy',
          });
        }
      }
       else {

        // For data files, use existing validation logic
        if (this.allowed_formats.includes(this.file_extension)) {
          if (
            evnt[0].file.size == this.maxFileSize ||
            evnt[0].file.size < this.maxFileSize
          ) {
            this.import_stage = 'upload_progress';
            this.Is_ValidFile = true;
            this.importError_Visible = false;
            this.checkfile(this.file)
          } else {
            this.Is_ValidFile = false;
            this.importError_Visible = true;
            this.importError_Message =
              'The specified file ' +
              this.file_name +
              ' exceeds the maximum upload size.';
            this.messageService.add({
              severity: 'error',
              summary: 'File Too Large',
              detail: this.importError_Message,
              key: 'app_notiy',
            });
          }
        } else {
          this.Is_ValidFile = false;
          this.importError_Visible = true;
          this.importError_Message = `Invalid file format! Supported formats are: ${this.allowed_formats.join(', ').toUpperCase()}`;
          this.messageService.add({
            severity: 'error',
            summary: 'Invalid File Format',
            detail: this.importError_Message,
            key: 'app_notiy',
          });
        }
      }
    } else {
      this.genericError_Visible = true;
      this.importError_Message =
        'Action not allowed! A file is still in progress';
    }
  }

  onBasicUploadAuto(event: any) {
      const selectedFiles = event.currentFiles;
    if (this.importType === 'all') {
      // Handle multiple files for 'all' type
      const files = event.files;
      if (files && files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          if (this.isValidFile(file)) {
            // Check for duplicates
            const isDuplicate = this.uploadedFiles.some(existingFile =>
              existingFile.name === file.name && existingFile.size === file.size
            );

            if (!isDuplicate) {
              const fileInfo = {
                file: file,
                name: file.name,
                size: file.size,
                type: file.type,
                preview: this.createFilePreview(file)
              };
              this.uploadedFiles.push(fileInfo);
            }
          }
        }
      }

      // Clear the file upload component to keep "Choose file" text
      setTimeout(() => {
        if (this.fileUpload) {
          this.fileUpload.clear();
        }
      }, 100);
    }
    else if (this.importType === 'image' ||this.importType === 'document') {
      const selectedFile = selectedFiles[0];
      this.file = selectedFile;
      this.file_extension = selectedFiles.length ? selectedFile.name.split('.')[1] : '';
      this.file_name = selectedFiles.length ? selectedFile.name : '';
      if (this.allowed_formats.includes(this.file_extension)) {
        this.import_stage = 'upload_progress';
        this.Is_ValidFile = true;
        this.importError_Visible = false;
          // Only create image preview for image files
          if (['jpeg', 'jpg', 'png'].includes(this.file_extension)) {
            this.createImagePreview(selectedFile);
          } else {
            this.imagePreviewUrl = ''; // Clear image preview for documents
          }
        } else {
          this.Is_ValidFile = false;
          this.importError_Visible = true;
          this.importError_Message = `Invalid file format! Supported formats are: ${this.allowed_formats.join(', ').toUpperCase()}`;
          this.imagePreviewUrl = ''; // Clear any previous image preview
          this.messageService.add({
            severity: 'error',
            summary: 'Invalid File Format',
            detail: this.importError_Message,
            key: 'app_notiy',
          });
        }
    }
     else {
      // For data files, handle single file (existing logic)
      const selectedFile = selectedFiles[0];
      this.file = selectedFile;
      this.file_extension = selectedFiles.length ? selectedFile.name.split('.')[1] : '';
      this.file_name = selectedFiles.length ? selectedFile.name : '';

            // For data files, use existing validation logic
      if (this.allowed_formats.includes(this.file_extension)) {
        if (
          selectedFile.size == this.maxFileSize ||
          selectedFile.size < this.maxFileSize
        ) {
          this.import_stage = 'upload_progress';
          this.Is_ValidFile = true;
          this.importError_Visible = false;
          this.checkfile(this.file)
        } else {
          this.Is_ValidFile = false;
          this.importError_Visible = true;
          this.importError_Message =
            'The specified file ' +
            this.file_name +
            ' exceeds the maximum upload size.';
          this.messageService.add({
            severity: 'error',
            summary: 'File Too Large',
            detail: this.importError_Message,
            key: 'app_notiy',
          });
        }
      } else {
        this.Is_ValidFile = false;
        this.importError_Visible = true;
        this.importError_Message = `Invalid file format! Supported formats are: ${this.allowed_formats.join(', ').toUpperCase()}`;
        this.messageService.add({
          severity: 'error',
          summary: 'Invalid File Format',
          detail: this.importError_Message,
          key: 'app_notiy',
        });
      }
    }
  }
  reloadGridData() {
    this.relaodGrid.emit(); // Emit event to parent
  }
  async checkfile(file: any){
    const formData = new FormData();
    formData.append('import_file', file);
    formData.append('is_preview', '1')
    const resp = await this.httpService.post(
      `/${this.moduleName}/import`,
      formData
    );
    if (resp?.status == 200 || resp?.status == 201) {
      resp?.data?.errors?.length >= 1 ? this.error_Visible = true : this.error_Visible = false;
      this.errors = resp?.data?.errors
    }else{
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: resp?.message,
        key: 'app_notiy',
      });
      this.Is_ValidFile = false
    }
  }

  async submit_file(file: any) {
    console.log(file, 'file');

    if (this.importType === 'image' || this.importType === 'all' || this.importType === 'document') {
      // For images and all file types, emit all uploaded files and close dialog
      if (this.uploadedFiles.length > 0) {
        // Emit all files
        this.relaodGrid.emit({
          files: this.uploadedFiles.map(f => f.file),
          file: this.uploadedFiles[0].file, // Keep backward compatibility
          imagePreviewUrl: this.imagePreviewUrl,
          fileName: this.file_name
        });
      } else if (file) {
        // Fallback to single file for backward compatibility
        this.relaodGrid.emit({
          file: file,
          imagePreviewUrl: this.imagePreviewUrl,
          fileName: this.file_name,
          fileType: this.importType
        });
      }
      this.hide();
    } else {
      // For data files, use existing upload logic
      const formData = new FormData();
      formData.append('import_file', file);
      const resp = await this.httpService.post(
        `/${this.moduleName}/import`,
        formData
      );
      if (resp?.status == 200 || resp?.status == 201) {
        this.confirmedUpload_ref.show();
        this.reloadGridData();
        this.error_Visible = false
        this.Is_ValidFile = false
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: resp?.error?.message,
          key: 'app_notiy',
        });
      }
      this.hide();
    }
  }

  // cancel upload
  uploadCancel() {
    this.Is_ValidFile = false;
    this.error_Visible = false;
    this.import_stage = 'ask_to_upload';
    this.value = 0;
    this.genericError_Visible = false;
    clearInterval(this.interval);
    this.fileUpload.clear();
  }

  // cancel popup
  cancel() {
    this.dialogService.dialogComponentRefMap.forEach((dialog) => {
      dialog.destroy();
    });
  }

  show() {
    this.show_ImportDialog = true;
  }

  hide() {
    this.show_ImportDialog = false;
    this.importError_Message = '';
    this.Is_ValidFile = false;
    this.error_Visible = false;
    this.value = 0;
    this.genericError_Visible = false;
    clearInterval(this.interval);
    this.importError_Visible = false;
    this.import_stage = 'ask_to_upload';
    this.dialog_header = 'Import File';
    this.fileUpload.clear();

    // Clear image data when dialog closes
    this.imagePreviewUrl = '';
    this.file = null;
    this.file_name = '';
    this.file_extension = '';

    // Don't clear uploadedFiles to maintain state
    this.uploadedFiles = [];
  }

  removeFile(index: number): void {
    if (index >= 0 && index < this.uploadedFiles.length) {
      const file = this.uploadedFiles[index];
      if (file.preview && file.preview.startsWith('blob:')) {
        URL.revokeObjectURL(file.preview);
      }
      this.uploadedFiles.splice(index, 1);
    }
  }


  clearAllFiles(): void {
    // Revoke any blob URLs first
    this.uploadedFiles.forEach(file => {
      if (file.preview && file.preview.startsWith('blob:')) {
        URL.revokeObjectURL(file.preview);
      }
    });

    // Then clear the list & reset validation
    this.uploadedFiles = [];
    this.Is_ValidFile = false;
  }


  setCurrentImage(imageUrl: string): void {
    this.imagePreviewUrl = imageUrl;
    this.import_stage = 'upload_progress';
    this.Is_ValidFile = true;
  }

  deleteCurrentImage() {
    this.imagePreviewUrl = '';
    this.file = [];
    this.file_name = '';
    this.file_extension = '';
    this.Is_ValidFile = false;
    this.import_stage = 'ask_to_upload'; // Reset the stage

  // Clear the file input component
  if (this.fileUpload) {
    this.fileUpload.clear();
  }

  // Clear any error messages
  this.importError_Visible = false;
  this.importError_Message = '';

  // Reset the value counter
  this.value = 0;
  }

  async downloadTemplate() {
    const resp = await this.httpService.post(`/${this.moduleName}/export`, {
      is_for_template: 1,
    });
    if (resp?.status == 200) {
      window.location.href = resp?.data?.link;
      this.messageService.add({
        severity: 'customSuccess',
        icon: 'i-check-circle',
        summary: 'Success',
        detail: resp?.message,
        key: 'app_notiy',
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: resp?.message,
        key: 'app_notiy',
      });
    }
  }

  getUploadButtonDisabled(): boolean {
    if (this.importType === 'image') {
      // For single image upload, check if we have a valid file OR uploaded files
      return !this.Is_ValidFile && this.uploadedFiles.length === 0;
    } else if (this.importType === 'all') {
      // For multiple file upload, check if we have uploaded files
      return this.uploadedFiles.length === 0;
    } else {
      // For other types (data, document), check if file is valid
      return !this.Is_ValidFile;
    }
  }
}
